import type { PurchaseOrder, PurchaseOrderLine } from '@/types/purchaseOrders';

export interface LineItemRow {
  partNumber: string | null;
  description: string;
  quantity: number;
  totalsByType: Record<string, number>;
  counted: boolean;
  unknown: boolean;
}

export interface LineItemsView {
  typeCodes: string[];
  rows: LineItemRow[];
  totals: Record<string, number>;
}

// A line's counts are unknown when the parser failed outright or met connector codes no
// termination type recognizes — an unknown must never render as a confident zero.
function isUnknown(line: PurchaseOrderLine): boolean {
  return line.countsPerUnit === null || line.unmappedConnectorCodes.length > 0;
}

export function buildLineItemsView(po: PurchaseOrder): LineItemsView {
  const typeCodes = [...Object.keys(po.terminationTotals)];
  po.lines.forEach((line) => {
    Object.keys(line.countsPerUnit ?? {}).forEach((typeCode) => {
      if (!typeCodes.includes(typeCode)) typeCodes.push(typeCode);
    });
  });

  const rows = po.lines.map<LineItemRow>((line) => {
    const unknown = isUnknown(line);
    const totalsByType = unknown
      ? {}
      : Object.fromEntries(
          Object.entries(line.countsPerUnit ?? {})
            .map(([typeCode, perUnit]): [string, number] => [typeCode, perUnit * line.quantity])
            .filter(([, total]) => total > 0),
        );
    return {
      partNumber: line.partNumber,
      description: line.description,
      quantity: line.quantity,
      totalsByType,
      counted: !unknown && Object.keys(totalsByType).length > 0,
      unknown,
    };
  });

  // The pinned row is the PO's full termination totals, read straight from
  // terminationTotals rather than re-summed from lines. This intentionally differs from the
  // schedule dialog's "Calculated" figure, which prefers remainingTotals — for a partially
  // scheduled PO that number is lower than the totals shown here.
  return { typeCodes, rows, totals: po.terminationTotals };
}
